import { ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'

export interface SliderProps {
  isOpen: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  width?: string
}

const Slider = ({
  isOpen,
  onClose,
  title,
  children,
  width = '70vw',
}: SliderProps) => {
  useEffect(() => {
    if (isOpen) {
      // Disable body scroll when slider is open
      document.body.style.overflow = 'hidden'
    } else {
      // Re-enable body scroll when slider is closed
      document.body.style.overflow = ''
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return createPortal(
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 transition-opacity duration-300"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Slider Panel */}
      <div
        className={`fixed right-0 top-0 z-40 h-screen transform bg-base-100 shadow-2xl transition-transform duration-300 ease-in-out min-w-96`}
        style={{
          width,
          transform: `translateX(${width})`,
          ...(isOpen && { transform: 'translateX(0)' }),
        }}
      >
        {isOpen && (
          <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-base-300 p-6">
              <h2 className="text-lg font-semibold">{title}</h2>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={onClose}
                aria-label="Close slider"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">{children}</div>
          </div>
        )}
      </div>
    </>,
    document.body,
  )
}

export { Slider }
